import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Badge } from './ui/badge';
import {
  ArrowLeft,
  DoorClosed,
  Car,
  Sun,
  Flame,
  PawPrint,
  Waves,
  Moon,
  Volume2,
  Backpack,
  ClipboardCheck,
  MapPin,
  ShoppingBag,
  Sparkles,
} from 'lucide-react';

const goldGradient = 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)';

const guidelineSections = [
  {
    id: 'driving',
    icon: Car,
    title: 'Driving on the Farm',
    body: [
      'Please drive slowly and carefully at all times — internal farm roads are shared with guests, children, our team, wildlife, and farm animals.',
      'Arriving after dark? Drive particularly slowly — internal roads are unlit and wildlife or farm animals may be nearby.',
      'Parking is available directly outside the cottages.',
    ],
  },
  {
    id: 'power',
    icon: Sun,
    title: 'Off-Grid Power System',
    body: [
      'Each cottage runs on an off-grid solar and battery system, so electricity works a little differently — please be mindful of how many appliances you run at once.',
      'Avoid boiling the kettle while hot water is being heavily used (especially during showers), and avoid running multiple high-power appliances at the same time.',
      'Switch off lights, fans, and appliances when you\u2019re not using them.',
      'Portable heaters are provided for cooler months — use them to warm your room for a short period, then switch off. Please don\u2019t leave heaters running continuously or overnight.',
      'If the power switches off (solar batteries can occasionally deplete after cloudy weather), a backup generator starts automatically. If an outage continues, please contact farm management.',
    ],
  },
  {
    id: 'animals',
    icon: PawPrint,
    title: 'Farm Animals & Paddocks',
    body: [
      'You\u2019ll share the farm with deer, sheep, a pony, and cows — we welcome you to enjoy seeing them!',
      'Never open or enter the animal gates or paddocks.',
      'Please ensure children are supervised around all animals at all times.',
      'Only feed animals approved food, and always treat them gently and respectfully.',
    ],
  },
  {
    id: 'facilities',
    icon: Waves,
    title: 'Facilities to Enjoy',
    body: [
      'Dam and picnic area',
      'Kayaks',
      'Fire pit',
      'Bushwalking trails (bring comfortable outdoor shoes or gumboots, especially after rain)',
      'Pool, spa, and tennis court',
      'Farm animals and surrounding paddocks',
    ],
  },
  {
    id: 'safety',
    icon: Moon,
    title: 'Water & Night-Time Safety',
    body: [
      'It\u2019s a large natural property with dams, bushland, and unlit paths after dark — a little extra care goes a long way.',
      'Strictly no swimming or entering the dam after dark.',
      'Supervise children around all water areas at all times.',
      'Walk back to your cottage together rather than alone, especially late at night, and use a torch or phone light on established paths.',
      'Please avoid wandering into paddocks, bushland, or remote areas after dark.',
    ],
  },
  {
    id: 'provided',
    icon: ShoppingBag,
    title: 'What\u2019s Provided in Your Cottage',
    body: [
      'Fresh bed linen, bath towels, and toiletries',
      'A starter supply of toilet paper, plus coffee and tea',
      'A fully equipped kitchen and BBQ for preparing meals',
      'Firewood for your stay',
    ],
  },
  {
    id: 'pack',
    icon: Backpack,
    title: 'What to Pack',
    body: [
      'Warm clothing and comfortable layers — evenings and early mornings can get crisp out in the bush.',
      'Comfortable outdoor shoes or gumboots for walking trails, especially after rain.',
      'Firewood and fire starters are provided, so settle in for crackling fireplaces and star-filled skies.',
    ],
  },
  {
    id: 'depart',
    icon: ClipboardCheck,
    title: 'Before You Leave',
    body: [
      'Wash, dry, and put away all dishes and kitchenware.',
      'Clean the BBQ if you used it.',
      'Remove rubbish and place it in the appropriate bins.',
      'Switch off lights, fans, heaters, and appliances.',
      'Leave your cottage reasonably clean and tidy, and double check for personal belongings.',
      'Close all doors and windows, and make sure the main farm gate is secured behind you.',
    ],
  },
];

const FarmGuidelines = () => {
  return (
    <main data-testid="farm-guidelines-page">
      {/* Page Header / Mini Hero */}
      <section className="relative py-20 sm:py-28 overflow-hidden bg-primary">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-primary/90" />
        <div
          className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}
        />
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              data-testid="farm-guidelines-back-home-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white transition-colors mb-8 focus-ring"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <Sparkles className="w-4 h-4 text-[#E6C76B]" />
              <span className="text-sm text-[#E6C76B] font-medium">A Kind Reminder</span>
            </div>

            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-white mb-4 max-w-3xl">
              Sherwood Farm{' '}
              <span
                style={{
                  backgroundImage: goldGradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Guest Guide
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl leading-relaxed">
              A few kind notes from the farm so everyone stays safe, comfortable, and can fully
              relax and celebrate with us. A little care from all of us helps protect the
              property, the animals, and this beautiful natural environment.
            </p>

            <div className="flex items-center gap-3 mt-8 text-white/70">
              <MapPin className="w-5 h-5 text-[#E6C76B] shrink-0" />
              <span className="text-base">123-125 Featherstones Rd, Sherwood NSW 2450</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 bg-background">
        <div className="section-container">
          {/* Directions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto mb-10"
            data-testid="farm-guidelines-directions"
          >
            <Alert className="gold-border">
              <MapPin className="h-5 w-5 text-primary" />
              <AlertTitle className="text-lg font-serif mb-2">Getting There</AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground space-y-2">
                <p>
                  The roads can be a little confusing through the bushland. From Red Range Road,
                  keep left at the junction with Featherstones Road and continue straight until
                  you see the Sherwood Farm sign and main gate on your left.
                </p>
                <p>
                  There are no shops on the property, so please stock up on groceries before you
                  arrive. The closest major supermarket is{' '}
                  <span className="font-medium text-foreground">
                    Woolworths Woolgoolga, 1851 Solitary Islands Way, Woolgoolga NSW 2456
                  </span>.
                </p>
              </AlertDescription>
            </Alert>
          </motion.div>

          {/* Highlighted must-knows */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-14">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Alert
                className="h-full border-2 border-primary bg-primary/5"
                data-testid="farm-guidelines-gate-alert"
              >
                <DoorClosed className="h-5 w-5 text-primary" />
                <AlertTitle className="text-lg font-serif mb-2 text-primary">
                  Always Secure the Main Gate
                </AlertTitle>
                <AlertDescription className="text-sm text-foreground/80">
                  When arriving or leaving, please open the gate and always close and secure it
                  behind you — this keeps the cows from escaping!
                </AlertDescription>
              </Alert>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Alert
                className="h-full border-2 border-primary bg-primary/5"
                data-testid="farm-guidelines-noise-alert"
              >
                <Volume2 className="h-5 w-5 text-primary" />
                <AlertTitle className="text-lg font-serif mb-2 text-primary">
                  Music & Noise Curfew
                </AlertTitle>
                <AlertDescription className="text-sm text-foreground/80">
                  Sound travels far in these quiet rural surroundings. From 9:00 PM please lower
                  outdoor music, with amplified music finishing by 10:00 PM. Celebrations can
                  continue afterward at a low, respectful level.
                </AlertDescription>
              </Alert>
            </motion.div>
          </div>

          {/* Fire safety callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto mb-14"
          >
            <Alert
              className="border-2 border-primary bg-primary/5"
              style={{
                backgroundImage: goldGradient,
                backgroundSize: '100% 4px',
                backgroundPosition: 'top',
                backgroundRepeat: 'no-repeat',
                paddingTop: '1.5rem',
              }}
              data-testid="farm-guidelines-fire-alert"
            >
              <div className="flex items-start gap-3">
                <Flame className="h-6 w-6 text-primary mt-1" />
                <div>
                  <AlertTitle className="text-lg font-serif mb-2 text-primary">
                    Candles, Fire Pits & Smoking
                  </AlertTitle>
                  <AlertDescription className="text-sm text-foreground/80">
                    Candles must be placed securely and never left unattended. Fire pits are
                    provided for your stay — please don't introduce additional open flames.
                    If smoking, please use the provided ashtrays and never leave cigarette butts
                    on the lawns, gardens, or paddocks.
                  </AlertDescription>
                </div>
              </div>
            </Alert>
          </motion.div>

          {/* Accordion for the rest */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <Accordion type="multiple" className="w-full" data-testid="farm-guidelines-accordion">
              {guidelineSections.map((section) => {
                const Icon = section.icon;
                return (
                  <AccordionItem key={section.id} value={section.id} className="border-primary/15">
                    <AccordionTrigger
                      data-testid={`farm-guidelines-accordion-trigger-${section.id}`}
                      className="text-left"
                    >
                      <span className="flex items-center gap-3 text-base font-serif text-primary no-underline">
                        <Icon className="w-5 h-5 text-primary shrink-0" />
                        {section.title}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pl-2">
                        {section.body.map((line, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="text-primary mt-1 font-bold">•</span>
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </motion.div>

          {/* Closing note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto mt-14 text-center"
          >
            <Badge
              variant="outline"
              className="mb-4 px-4 py-1.5 border-primary/30 text-primary"
            >
              Most Importantly
            </Badge>
            <p className="text-lg font-serif text-primary max-w-2xl mx-auto leading-relaxed">
              These notes are here so everyone can settle in, celebrate, laugh, and create
              memories — safely, respectfully, and comfortably. Thank you for helping us look
              after this special place.
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default FarmGuidelines;
